import { useState } from 'react';
import type {
    BulkDeleteResult,
    BulkSelectionPayload,
} from '@/services/engine/types';

type BulkRequest = { ids: string[] } | { described: true };

interface UseDirectoryDeleteFlowOptions {
    bulkDeleteItems?: (
        selection: BulkSelectionPayload,
        filters?: Record<string, unknown>,
    ) => Promise<BulkDeleteResult>;
    describeSelection?: () => {
        selection: BulkSelectionPayload;
        filters?: Record<string, unknown>;
    };
    onSuccess: () => Promise<void>;
    onDeleteSuccess?: (deleted: number) => void;
    onDeleteError?: (error: unknown) => void;
    onPartialDelete?: (result: BulkDeleteResult) => void;
    onDeleteInProgress?: () => void;
}

export function useDirectoryDeleteFlow({
    bulkDeleteItems,
    describeSelection,
    onSuccess,
    onDeleteSuccess,
    onDeleteError,
    onPartialDelete,
    onDeleteInProgress,
}: UseDirectoryDeleteFlowOptions) {
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
    const [bulkRequest, setBulkRequest] = useState<BulkRequest | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDeleteClick = (itemId: string) => {
        setDeleteConfirmId(itemId);
    };

    const requestBulkDelete = (ids: string[]) => {
        if (ids.length) setBulkRequest({ ids });
    };

    const requestDescribedBulkDelete = () => {
        setBulkRequest({ described: true });
    };

    const runBulk = async (request: BulkRequest): Promise<boolean> => {
        if (isDeleting) return false;
        setIsDeleting(true);
        try {
            if (!bulkDeleteItems) return false;

            let result: BulkDeleteResult;
            if ('described' in request) {
                if (!describeSelection) return false;
                const { selection, filters } = describeSelection();
                result = await bulkDeleteItems(selection, filters);
            } else {
                result = await bulkDeleteItems({ ids: request.ids });
            }

            try {
                await onSuccess();
            } catch (error) {
                onDeleteError?.(error);
                return false;
            }

            if (result.inProgress) {
                onDeleteInProgress?.();
                return true;
            }
            if (result.failed > 0 && result.deleted > 0) {
                onPartialDelete?.(result);
                return true;
            }
            if (result.failed > 0) {
                onDeleteError?.(new Error('Bulk delete failed'));
                return false;
            }
            onDeleteSuccess?.(result.deleted);
            return true;
        } catch (error) {
            onDeleteError?.(error);
            return false;
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirmId) return;
        const succeeded = await runBulk({ ids: [deleteConfirmId] });
        if (succeeded) setDeleteConfirmId(null);
    };

    const handleBulkDeleteConfirm = async (): Promise<boolean> => {
        if (!bulkRequest) return false;
        try {
            return await runBulk(bulkRequest);
        } finally {
            setBulkRequest(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmId(null);
    };

    const handleBulkDeleteCancel = () => {
        setBulkRequest(null);
    };

    return {
        deleteConfirmId,
        bulkRequest,
        isDeleting,
        handleDeleteClick,
        requestBulkDelete,
        requestDescribedBulkDelete,
        handleDeleteConfirm,
        handleBulkDeleteConfirm,
        handleDeleteCancel,
        handleBulkDeleteCancel,
    };
}
